import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LOGO_LIGHT } from '../App';
import { Lock, Eye, EyeOff, CheckCircle, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../config/api';

const API = API_URL;

// Splash image URL
const SPLASH_IMAGE = '/splash-bg.webp';
// Desktop background image
const DESKTOP_BG_IMAGE = 'https://customer-assets.emergentagent.com/job_abc8b3a8-9991-4796-b26e-2900581cbdb1/artifacts/f1f29esj_screen_desktop.webp';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Link non valido. Richiedi un nuovo reset password.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Le password non corrispondono');
      return;
    }
    
    if (password.length < 6) {
      setError('La password deve essere di almeno 6 caratteri');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/api/auth/reset-password`, {
        token,
        password
      });
      
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante il reset della password');
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (success) {
    return (
      <div 
        className="w-full fixed inset-0 flex flex-col overflow-hidden"
        style={{ height: '100dvh', minHeight: '-webkit-fill-available' }}
      >
        <img src={SPLASH_IMAGE} alt="" className="auth-page-bg auth-mobile-only" style={{ filter: 'brightness(0.7)' }} />
        <img src={DESKTOP_BG_IMAGE} alt="" className="auth-page-desktop-bg" style={{ filter: 'brightness(0.7)' }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/80" />

        <div className="relative z-10 flex-1 flex flex-col justify-center items-center px-5">
          {/* Glass card */}
          <div className="w-full max-w-md backdrop-blur-xl bg-black/25 rounded-3xl p-6 border border-white/10 shadow-2xl">
            <div className="text-center">
              <div className="bg-green-500/20 backdrop-blur-sm rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 border border-green-500/30">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">Password Reimpostata!</h2>
              <p className="text-white/80 mb-8">
                La tua password è stata cambiata con successo. Ora puoi accedere con la nuova password.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="w-full py-3.5 rounded-2xl font-semibold bg-[#E8334A] text-white"
                data-testid="go-to-login-btn"
              >
                Vai al Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="w-full fixed inset-0 flex flex-col overflow-hidden"
      style={{ height: '100dvh', minHeight: '-webkit-fill-available' }}
    >
      <img src={SPLASH_IMAGE} alt="" className="auth-page-bg auth-mobile-only" style={{ filter: 'brightness(0.7)' }} />
      <img src={DESKTOP_BG_IMAGE} alt="" className="auth-page-desktop-bg" style={{ filter: 'brightness(0.7)' }} />
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/80" />

      <div className="relative z-10 flex-1 flex flex-col justify-center items-center px-5"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 16px)' }}
      >
        {/* Glass card */}
        <div className="w-full max-w-md backdrop-blur-xl bg-black/40 rounded-3xl p-6 border border-white/10 shadow-2xl">
          {/* Logo */}
          <div className="text-center mb-5">
            <img 
              src={LOGO_LIGHT} 
              alt="Slimmy"
              className="h-9 mx-auto mb-1.5 drop-shadow-lg"
            />
            <p className="text-white/90 text-sm font-medium">
              Reimposta la tua password
            </p>
          </div>

          {!token ? (
            <div className="text-center">
              <div className="bg-red-500/20 backdrop-blur-sm rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 border border-red-500/30">
                <AlertTriangle className="w-8 h-8 text-red-400" />
              </div>
              <p className="text-white/80 mb-6">{error}</p>
              <button
                onClick={() => navigate('/login')}
                className="w-full py-3.5 rounded-2xl font-semibold bg-white/20 text-white"
              >
                Torna al Login
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* New Password Input */}
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70" size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Nuova password"
                  className="w-full pl-12 pr-12 py-3.5 rounded-2xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[#E8334A]/50 focus:border-transparent focus:bg-white/15"
                  data-testid="reset-password-input"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              {/* Confirm Password Input */}
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70" size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Conferma password"
                  className="w-full pl-12 pr-4 py-3.5 rounded-2xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[#E8334A]/50 focus:border-transparent focus:bg-white/15"
                  data-testid="reset-confirm-password-input"
                  required
                  minLength={6}
                />
              </div>

              {/* Error */}
              {error && (
                <div className="bg-red-500/20 text-red-200 p-3 rounded-xl text-sm text-center border border-red-500/30">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!password || !confirmPassword || loading}
                className={`w-full py-3.5 rounded-2xl font-semibold flex items-center justify-center gap-2 transition-all ${
                  password && confirmPassword && !loading
                    ? 'bg-[#E8334A] text-white active:scale-[0.98]'
                    : 'bg-white/20 text-white/50 cursor-not-allowed'
                }`}
                data-testid="reset-submit-btn"
              >
                {loading ? (
                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                ) : (
                  'Reimposta Password'
                )}
              </button>
            </form>
          )}

          {/* Back to login */}
          <p className="text-white/50 text-sm mt-4 text-center">
            <button 
              onClick={() => navigate('/login')}
              className="text-white font-medium hover:underline"
            >
              Torna al Login
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
